import { useCallback, useEffect, useRef, useState } from 'react';
import { dailyCoachRetryable, dailyCoachRetryGuidance, dailyCoachRetryDenied } from './dailyCoachFailure';

const UNCERTAINTY_DELAY_MS = 30000;
const uncertaintyMessage = 'Could not confirm the daily insight retry. You can safely resend the same retry for this saved check-in.';
const retryGeneration = status => status?.external_daily_task?.retry_generation ?? status?.external_daily_capture?.retry_generation ?? 0;

// Owner-scoped receipts reconcile a Retry by its durable explicit generation.
// An unchanged failure or a failed read cannot prove that its POST did not commit.
export default function useDailyCoachTaskStatus(backendService) {
  const [snapshot, setSnapshot] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState(null);
  const [uncertaintyExpired, setUncertaintyExpired] = useState(false);
  const uncertaintyTimer = useRef(null);
  const owner = backendService.positionOwner;
  const identity = backendService.authenticatedIdentity;
  const accountGeneration = backendService.backgroundIdentityGeneration;
  const lifecycle = useRef(null);
  const serviceRef = useRef(backendService);
  serviceRef.current = backendService;
  const operation = useRef(null);
  const generation = useRef(0);
  const current = useCallback(scope => scope && lifecycle.current === scope && scope.active
    && scope.service === serviceRef.current && scope.owner === scope.service.positionOwner
    && scope.identity === scope.service.authenticatedIdentity && scope.accountGeneration === scope.service.backgroundIdentityGeneration, []);
  const resetOperation = useCallback(() => {
    clearTimeout(uncertaintyTimer.current);
    operation.current = null;
    setUncertaintyExpired(false);
    setRetrying(false);
    setRetryError(null);
  }, []);
  const refresh = useCallback(async () => {
    const scope = lifecycle.current;
    if (!current(scope) || scope.service !== backendService) return;
    const token = ++generation.current;
    try {
      const status = await backendService.getLatestStatus();
      if (!current(scope) || token !== generation.current) return;
      const op = operation.current;
      if (op && (status?.is_today === false || (status?._id && String(status._id) !== op.statusId))) {
        resetOperation();
      } else if (op && String(status?._id) === op.statusId && (op.acknowledged || retryGeneration(status) > op.expectedGeneration
        || (status?.external_daily_task?.status === 'consumed' && status.external_daily_task.insight))) {
        resetOperation();
      }
      if (status || !operation.current) setSnapshot(status?.is_today === false ? null : status || null);
      return status;
    } catch {
      // Keep the last receipt and unresolved operation; polling must survive a
      // read outage, including an outage after a lost Retry acknowledgement.
      return undefined;
    }
  }, [backendService, current, resetOperation]);
  useEffect(() => {
    const activate = () => {
      if (lifecycle.current) lifecycle.current.active = false;
      generation.current += 1;
      lifecycle.current = { service: backendService, owner: backendService.positionOwner, identity: backendService.authenticatedIdentity,
        accountGeneration: backendService.backgroundIdentityGeneration, active: true };
      resetOperation();
      setSnapshot(null);
    };
    activate();
    refresh();
    const clear = () => { activate(); };
    const newStatus = () => { activate(); refresh(); };
    const changed = () => { refresh(); };
    const visible = () => { if (document.visibilityState !== 'hidden') refresh(); };
    const unsubscribe = backendService.onAccountScopeChanged?.(newStatus);
    window.addEventListener('coach-conversation-cleared', clear);
    window.addEventListener('daily-status-saved', newStatus);
    window.addEventListener('daily-coach-task-changed', changed);
    document.addEventListener('visibilitychange', visible);
    return () => {
      lifecycle.current.active = false;
      generation.current += 1;
      operation.current = null;
      clearTimeout(uncertaintyTimer.current);
      unsubscribe?.();
      window.removeEventListener('coach-conversation-cleared', clear);
      window.removeEventListener('daily-status-saved', newStatus);
      window.removeEventListener('daily-coach-task-changed', changed);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [backendService, owner, identity, accountGeneration, refresh, resetOperation]);
  const state = snapshot?.external_daily_task?.status;
  const renewing = snapshot?.external_daily_task?.auto_retry === true;
  const settledPending = snapshot?.external_daily_capture_pending === true || ['queued', 'claimed', 'completed'].includes(state) || renewing;
  const settledFailed = !settledPending && (Boolean(snapshot?.external_daily_failure_code || snapshot?.external_daily_task?.failure_code)
    || ['failed', 'invalidated', 'cancelled', 'timedout'].includes(state));
  const pending = settledPending || retrying;
  const retryUncertain = uncertaintyExpired && !settledPending;
  const failed = settledFailed && !retrying && !retryUncertain;
  const retryable = failed && dailyCoachRetryable(snapshot);
  useEffect(() => { if (settledPending && !retrying) setRetryError(null); }, [settledPending, retrying]);
  const retry = useCallback(async () => {
    const scope = lifecycle.current;
    if (!current(scope)) return;
    const previous = operation.current;
    if (previous ? (!previous.resendReady || previous.sending || settledPending) : (!retryable || !snapshot?._id)) return;
    // A deadline permits replay, never a new operation or a new generation.
    const op = previous || { scope, statusId: String(snapshot._id), expectedGeneration: retryGeneration(snapshot) };
    op.sending = true;
    op.resendReady = false;
    clearTimeout(uncertaintyTimer.current);
    generation.current += 1;
    setUncertaintyExpired(false);
    operation.current = op;
    const active = () => current(scope) && operation.current === op;
    setRetrying(true);
    setRetryError(null);
    try {
      await backendService.retryDailyCoachInsight(op.statusId, op.expectedGeneration);
      if (!active()) return;
      // This exact POST was acknowledged, including reuse of automatic work
      // that spends no explicit generation. Unknown ACKs never take this path.
      op.acknowledged = true;
      window.dispatchEvent(new Event('daily-coach-task-changed'));
    } catch (error) {
      if (!active()) return;
      const code = error?.response?.data?.code;
      if (dailyCoachRetryDenied(code)) {
        resetOperation();
      } else {
        op.sending = false;
        // Start before awaiting the GET: reconciliation itself can hang.
        uncertaintyTimer.current = setTimeout(() => {
          if (!active()) return;
          op.resendReady = true;
          setUncertaintyExpired(true);
          setRetrying(false);
          setRetryError(uncertaintyMessage);
        }, UNCERTAINTY_DELAY_MS);
      }
      setRetryError(dailyCoachRetryGuidance(code));
      await refresh();
    }
  }, [backendService, current, refresh, resetOperation, retryable, settledPending, snapshot]);
  useEffect(() => {
    if (!pending && !uncertaintyExpired) return undefined;
    const interval = setInterval(() => {
      if (document.visibilityState !== 'hidden') refresh();
    }, 30000);
    return () => clearInterval(interval);
  }, [pending, uncertaintyExpired, refresh]);
  return { pending, failed, status: snapshot, refresh, retry, retrying, retryError, retryable, retryUncertain };
}
